// Lane B4's screens, registered the way every lane registers its own (lane contract C-29; C-B4-7 retires the settings
// case App.tsx carried on the lane):
//
//   #settings/framework[/<level>]   Settings › the TA-owned interview framework by level (#settings/framework opens
//                                   Manager & Below), for a reader of configuration. It claims exactly that sub-route of
//                                   B1's #settings view (specificity 1, above B1's whole-view pattern); a level the
//                                   framework does not know, or a seat whose manifest lacks `config.list`, is the
//                                   not-found screen. The page keeps the McRoot it drew under App.tsx.
//   #requisitions/R-####/intake     the requisition's Intake & Panel tab (UAT D-10): B2's requisition detail with its
//                                   header, track and tabs, and under them the intake page's body without V1's header
//                                   (views/Intake.tsx IntakeTabBody), registered the way lane B3 registers Candidates
//                                   (registerRequisitionTab, which registers the tab's route and replaces V1's link);
//                                   shown where the person may read THIS requisition's intake (DC-235, below).
//   #requisitions/R-####/intake/part0|part1|call
//                                   the same tab opened on that hand-off (F-186): the intake call page folded into the
//                                   requisition's header and tabs, its Print in the header's Actions menu. Part 2 is the
//                                   requisition's Plan tab, so `…/intake/part2` keeps V1's page at App.tsx's route; so
//                                   does the call for a seat whose intake read is refused (the Coordinator's scheduling
//                                   card, views/Intake.tsx).
//
// One seat check (DC-235; UAT F-151, F-160): the tab is offered only where its read is allowed. The manifest says the
// person may call intake.open on some requisition; the requisition's own `can.intake` (requisition.open) says whether
// they hold a seat on THIS one's intake — the server computes it with intake.open's seat (intake_reader: the seated
// readers, a Coordinator in the requisition's pool, a TA Manager over it), so an out-of-pool Coordinator is never offered
// a tab that opens on a refusal. A read that sends no `intake` flag keeps the manifest's answer.
//
// The tab's route is guarded above the one registerRequisitionTab registers (specificity 2): a seat whose manifest lacks
// intake.open, or whose intake read is refused, meets the not-found screen (or the archived snapshot) as V1's page gave
// it, never the requisition's header over a refusal and never its Requisition tab in the tab's place.

import { createElement } from "react";
import { intakeOpen, type RequisitionFull } from "@/api/tools";
import type { AppError } from "@/api/runtime";
import { isClientRefusal, useRead } from "@/api/useTool";
import { V1Slot } from "@/shell/island";
import { registerView, routePath, type ViewProps } from "@/shell/viewRegistry";
import { useSession } from "@/session/useSession";
import { ObjectReadGuard } from "@/ui";
import { IntakePage, IntakeTabBody } from "@/views/Intake";
import { NotFound } from "@/views/NotFound";
import { FrameworkConfig, frameworkLevelOk } from "@/views/plan/FrameworkConfig";
import { REQ_LABELS } from "@/views/requisition/labels";
import { registerRequisitionTab, RequisitionRoute } from "@/views/requisition/RequisitionDetail";
import type { RequisitionTabProps } from "@/views/requisition/slots";

/** `settings/framework` and `settings/framework/<level>` exactly: nothing after the level. */
export const FRAMEWORK_ROUTE = /^settings\/framework(?:\/[^/]+)?$/;

/** `requisitions/R-####/intake` and its hand-offs `…/intake/part0|part1|call`, the id and the part captured; Part 2
 * (`…/intake/part2`) keeps V1's page, which opens the interview plan. */
export const INTAKE_TAB_ROUTE = /^requisitions\/(R-\d+)\/intake(?:\/(part0|part1|call))?$/;

/** The Intake & Panel tab's place among the requisition's tabs: after the Requisition tab (0), before Candidates (30). */
export const INTAKE_TAB_ORDER = 20;

function FrameworkRoute({ route }: ViewProps) {
  const { can } = useSession();
  const level = route.parts[1];
  if (!frameworkLevelOk(level) || !can("config.list")) return createElement(NotFound, { route: route.raw });
  return createElement(FrameworkConfig, { key: level ?? "", level });
}

/** What the intake tab's route draws: the not-found screen for a seat without intake.open, the read's own screen when
 * the intake read is refused (not found, or the archived snapshot), else the requisition detail with the tab open. */
export function intakeRouteDraws(mayOpen: boolean, error: AppError | null): "not-found" | "refusal" | "requisition" {
  if (!mayOpen) return "not-found";
  return error && isClientRefusal(error) ? "refusal" : "requisition";
}

function IntakeRoute({ route }: ViewProps) {
  const { can } = useSession();
  const [, id = "", part] = INTAKE_TAB_ROUTE.exec(routePath(route)) ?? [];
  const mayOpen = can("intake.open");
  // the tab's body reads the same query, so the requisition detail opens on it without a second call
  const q = useRead(["intake", id], () => intakeOpen({ requisition_id: id }), { enabled: mayOpen });
  const draws = intakeRouteDraws(mayOpen, q.error);
  // a seat that may not read the intake still schedules its call: V1's call page draws the scheduling card, as it did
  if (part === "call" && draws !== "requisition") return createElement(V1Slot, null, createElement(IntakePage, { key: id, id, part }));
  if (draws === "not-found") return createElement(NotFound, { route: route.raw });
  if (draws === "refusal") return createElement(ObjectReadGuard, { query: q });
  return createElement(RequisitionRoute, { route });
}

/** DC-235's one seat check for the tab: the manifest's intake.open and the requisition's own seat on its intake. */
export function intakeTabVisible(can: (tool: string) => boolean, r: Pick<RequisitionFull, "can">): boolean {
  return can("intake.open") && r.can?.["intake"] !== false;
}

function IntakeTab({ r, rest }: RequisitionTabProps) {
  // F-185, F-186: the hand-off the person chose — the route's part (`…/intake/call`), or an older `?part=` link — drawn in
  // place under the requisition's header and tabs; the requisition's route re-renders the tab on every hash change, so
  // the address is read here (none under SSR)
  const query = typeof window === "undefined" ? "" : window.location.hash.split("?")[1] ?? "";
  const part = rest[0]?.split("?")[0] || new URLSearchParams(query).get("part") || "";
  return createElement(IntakeTabBody, { key: r.id, id: r.id, part });
}

registerView(FRAMEWORK_ROUTE, FrameworkRoute, { lane: "B4", specificity: 1 });
registerRequisitionTab({
  key: "intake",
  label: REQ_LABELS.intakePanel,
  lane: "B4",
  order: INTAKE_TAB_ORDER,
  component: IntakeTab,
  visible: intakeTabVisible,
});
registerView(INTAKE_TAB_ROUTE, IntakeRoute, { lane: "B4", specificity: 2 });
